// Intake hand-over: convert an enquiry (if needed), transfer the contact to
// the salesperson through the audited transfer endpoint, and optionally book
// a meeting with that salesperson as attendee. Each step uses an existing
// permission-checked API; nothing here widens access.
export interface HandoverTargetLike { user_id: number; vertical_id: number; name: string }
export interface HandoverInput {
  target: HandoverTargetLike;
  leadId?: number;
  personId?: number;
  note: string;
  meeting?: { at: string; title: string; location: string } | null;
}
export interface HandoverDeps {
  convertLead: (id: number, body: Record<string, unknown>) => Promise<{ person_id: number }>;
  getPerson: (id: number) => Promise<{ id: number; owner_user_id: number | null; vertical_id: number; company_id: number | null }>;
  transfer: (id: number, body: { owner_user_id: number; vertical_id: number; note: string; move_open_deals: boolean }) => Promise<unknown>;
  createMeeting: (body: Record<string, unknown>) => Promise<unknown>;
  now?: () => number;
}

// A datetime-local value entered in India time → an absolute ISO timestamp.
export function indiaLocalToISO(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}:00+05:30`);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
}

export async function runHandover(input: HandoverInput, deps: HandoverDeps): Promise<{ personId: number; transferred: boolean; meetingBooked: boolean }> {
  const note = input.note.trim();
  if (!note) throw new Error('Add a short hand-over note for the salesperson.');
  if (!input.leadId && !input.personId) throw new Error('Choose an enquiry or contact to hand over.');
  let scheduledAt: string | null = null;
  if (input.meeting) {
    scheduledAt = indiaLocalToISO(input.meeting.at);
    if (!scheduledAt || Date.parse(scheduledAt) <= (deps.now ?? Date.now)()) throw new Error('Choose a future meeting date and time (IST).');
  }
  const personId = input.personId ?? (await deps.convertLead(input.leadId!, { vertical_id: input.target.vertical_id })).person_id;
  const person = await deps.getPerson(personId);
  let transferred = false;
  if (person.owner_user_id !== input.target.user_id || person.vertical_id !== input.target.vertical_id) {
    // Open deals travel with the contact: a cross-pipeline move may not leave them behind.
    await deps.transfer(personId, { owner_user_id: input.target.user_id, vertical_id: input.target.vertical_id, note, move_open_deals: true });
    transferred = true;
  }
  if (input.meeting && scheduledAt) {
    await deps.createMeeting({
      type: 'meeting', status: 'scheduled', person_id: personId, vertical_id: input.target.vertical_id, company_id: person.company_id,
      assignee_user_id: input.target.user_id, scheduled_at: scheduledAt, title: input.meeting.title.trim() || 'Meeting', location: input.meeting.location.trim(), body: note
    });
  }
  return { personId, transferred, meetingBooked: !!input.meeting };
}
